import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Receipt, Plus, Trash2, Check, Pencil, X, AlertTriangle, Clock, Search, Copy, ChevronUp, ChevronDown, ArrowUpDown, Landmark, Download, Settings, Square, CheckSquare, Upload, Sparkles, FileText, Loader2, Mail } from "lucide-react";

const GREEN = "#34D399";
const RED = "#F87171";
const AMBER = "#FBBF24";

// Invoices are inkl. moms with øre — show 2 decimals
const kr = (n) => new Intl.NumberFormat("da-DK", { style: "currency", currency: "DKK", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const amountToInput = (n) => (n || 0).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const normSup = (s) => (s || "").toLowerCase()
  .replace(/[^a-z0-9æøå ]/g, " ")
  .replace(/\b(a\/s|as|aps|ivs|ab|a|s)\b/g, " ")
  .replace(/\s+/g, " ").trim();

const MON = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
const parse = (s) => { if (!s) return null; const [y, m, d] = s.split("-").map(Number); const dt = new Date(y, m - 1, d); dt.setHours(0, 0, 0, 0); return dt; };
const fmtDate = (s) => { const d = parse(s); return d ? `${d.getDate()}. ${MON[d.getMonth()]} ${d.getFullYear()}` : "—"; };
const todayMidnight = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const parseAmount = (v) => {
  let s = String(v).trim();
  if (!s) return NaN;
  if (s.includes(",")) {
    // Danish: dots are thousands separators, comma is decimal
    s = s.replace(/\./g, "").replace(",", ".");
  } else {
    const dots = (s.match(/\./g) || []).length;
    if (dots === 1) {
      const dec = s.split(".")[1];
      if (dec.length === 3) s = s.replace(/\./g, ""); // e.g. 1.234 = thousands
      // otherwise keep the single dot as a decimal point (1234.5 / 1234.56)
    } else if (dots > 1) {
      s = s.replace(/\./g, ""); // e.g. 1.234.567 = thousands
    }
  }
  return parseFloat(s);
};

function billStatus(b) {
  if (b.paid) return "paid";
  const due = parse(b.due_date);
  if (!due) return "open";
  const today = todayMidnight();
  const diff = Math.round((due - today) / 86400000);
  if (diff < 0) return "overdue";
  if (diff <= 7) return "soon";
  return "open";
}

function Tile({ title, value, sub, color, icon, testId }) {
  return (
    <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid={testId}>
      <h3 className="text-[13px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2 flex items-center gap-2">{icon}{title}</h3>
      <div className="font-heading text-3xl font-bold tabular-nums" style={{ color: color || "#F8FAFC" }}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

const emptyForm = { supplier: "", invoice_number: "", amount: "", due_date: "", note: "", creditor_account: "" };

export function BillsStats({ scope = "admin" } = {}) {
  const P = scope === "dangoods" ? "/dangoods" : "";
  const SUPPLIERS_URL = scope === "dangoods" ? "/dangoods/bill-suppliers" : "/suppliers";
  const SAVE_ACCOUNT_URL = scope === "dangoods" ? "/dangoods/bill-suppliers/save-account" : "/suppliers/save-account";
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [sort, setSort] = useState({ key: "due_date", dir: "asc" });

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`${P}/bills`); setBills(data); }
    catch { toast.error("Kunne ikke hente regninger"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const [supplierMap, setSupplierMap] = useState({});
  const [bankSettings, setBankSettings] = useState({ own_account: scope === "dangoods" ? "" : "68580001110332", product_code: "ERH356" });
  const [selected, setSelected] = useState(() => new Set());
  const [bankDlg, setBankDlg] = useState(false);
  const [bankForm, setBankForm] = useState({ own_account: "", product_code: "ERH356" });

  const [acctEdit, setAcctEdit] = useState(null);
  const [acctVal, setAcctVal] = useState("");

  const loadSuppliers = useCallback(() => {
    api.get(SUPPLIERS_URL).then(({ data }) => { const m = {}; data.forEach((s) => { if (s.bank_account) m[s.name.trim().toLowerCase()] = s.bank_account; }); setSupplierMap(m); }).catch(() => {});
  }, []);

  const [drafts, setDrafts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dEdits, setDEdits] = useState({});

  const loadDrafts = useCallback(async () => {
    try {
      const { data } = await api.get(`${P}/bill-drafts`);
      setDrafts(data);
      setDEdits((prev) => { const n = {}; data.forEach((d) => { n[d.id] = prev[d.id] || { supplier: d.supplier || "", invoice_number: d.invoice_number || "", amount: d.amount ? amountToInput(d.amount) : "", due_date: d.due_date || "", creditor_account: d.creditor_account || "" }; }); return n; });
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadSuppliers();
    loadDrafts();
    api.get(`${P}/bank-settings`).then(({ data }) => setBankSettings(data)).catch(() => {});
  }, [loadSuppliers, loadDrafts]);

  // Auto-hent kontonr. fra leverandørregistret hvis udkastet mangler det
  const supplierAccountFor = useCallback((name) => {
    const exact = supplierMap[(name || "").trim().toLowerCase()];
    if (exact) return exact;
    const t = normSup(name);
    if (t.length < 3) return "";
    for (const k in supplierMap) {
      const nk = normSup(k);
      if (nk && (nk === t || (nk.length >= 4 && t.length >= 4 && (t.includes(nk) || nk.includes(t))))) return supplierMap[k];
    }
    return "";
  }, [supplierMap]);

  useEffect(() => {
    setDEdits((prev) => {
      let changed = false; const n = { ...prev };
      drafts.forEach((d) => {
        const e = n[d.id]; if (!e) return;
        if (!(e.creditor_account || "").trim()) {
          const acc = supplierAccountFor(e.supplier);
          if (acc) { n[d.id] = { ...e, creditor_account: acc }; changed = true; }
        }
      });
      return changed ? n : prev;
    });
  }, [supplierAccountFor, drafts]);

  const onUploadInvoice = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`${P}/bills/extract`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Faktura læst — tjek udkastet nedenfor");
      await loadDrafts();
    } catch (err) { toast.error(err?.response?.data?.detail || "Kunne ikke læse fakturaen"); }
    finally { setUploading(false); }
  };

  const setDField = (id, k, v) => setDEdits((p) => {
    const cur = { ...p[id], [k]: v };
    if (k === "supplier" && !(cur.creditor_account || "").trim()) {
      const acc = supplierAccountFor(v);
      if (acc) cur.creditor_account = acc;
    }
    return { ...p, [id]: cur };
  });

  const findDuplicate = (invNo) => {
    const n = (invNo || "").trim().toLowerCase();
    if (!n) return null;
    return bills.find((b) => (b.invoice_number || "").trim().toLowerCase() === n) || null;
  };

  const approveDraft = async (d) => {
    const ed = dEdits[d.id] || {};
    const amt = parseAmount(ed.amount);
    if (!(ed.supplier || "").trim()) { toast.error("Indtast leverandør"); return; }
    if (!amt || amt <= 0) { toast.error("Indtast et gyldigt beløb"); return; }
    if (!ed.due_date) { toast.error("Vælg en forfaldsdato"); return; }
    const dup = findDuplicate(ed.invoice_number);
    if (dup && !window.confirm(`Fakturanr. "${(ed.invoice_number || "").trim()}" findes allerede på listen (${dup.supplier}, ${amountToInput(dup.amount)} kr.).\n\nGodkend alligevel?`)) return;
    try {
      await api.post(`${P}/bill-drafts/${d.id}/approve`, { supplier: ed.supplier.trim(), invoice_number: (ed.invoice_number || "").trim(), amount: amt, due_date: ed.due_date, paid: false, note: "", creditor_account: (ed.creditor_account || "").trim() });
      toast.success("Regning lagt på betalingslisten");
      await loadDrafts(); await load(); loadSuppliers();
    } catch { toast.error("Kunne ikke godkende udkast"); }
  };

  const discardDraft = async (id) => {
    if (!window.confirm("Afvis dette udkast?")) return;
    try { await api.delete(`${P}/bill-drafts/${id}`); await loadDrafts(); toast.success("Udkast afvist"); }
    catch { toast.error("Kunne ikke afvise"); }
  };

  const draftFileUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}/api${P}/bill-drafts/file/${path}?auth=${localStorage.getItem("cf_token")}`;

  const saveAccount = async (b, acc) => {
    const v = (acc || "").trim();
    if (!v) { toast.error("Indtast kontonr."); return; }
    try {
      await api.put(`${P}/bills/${b.id}`, { supplier: b.supplier, invoice_number: b.invoice_number || "", amount: b.amount, due_date: b.due_date, paid: b.paid, note: b.note || "", creditor_account: v });
      await api.post(SAVE_ACCOUNT_URL, { name: b.supplier, bank_account: v });
      setAcctEdit(null); setAcctVal("");
      toast.success("Kontonr. gemt");
      await load(); loadSuppliers();
    } catch { toast.error("Kunne ikke gemme kontonr."); }
  };

  const onSupplierChange = (v) => setForm((f) => ({ ...f, supplier: v, creditor_account: f.creditor_account || supplierMap[v.trim().toLowerCase()] || "" }));
  const ddmmyyyy = (s) => { const d = parse(s); return d ? `${String(d.getDate()).padStart(2, "0")}${String(d.getMonth() + 1).padStart(2, "0")}${d.getFullYear()}` : ""; };
  const toggleSel = (id) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const openBankSettings = () => { setBankForm(bankSettings); setBankDlg(true); };
  const saveBankSettings = async () => { try { await api.put(`${P}/bank-settings`, bankForm); setBankSettings(bankForm); setBankDlg(false); toast.success("Bankindstillinger gemt"); } catch { toast.error("Kunne ikke gemme"); } };
  const toggleAllPayable = () => {
    const ids = bills.filter((b) => !b.paid && (b.creditor_account || "").trim()).map((b) => b.id);
    setSelected((prev) => { const all = ids.length > 0 && ids.every((id) => prev.has(id)); return new Set(all ? [] : ids); });
  };
  const buildBankFile = () => {
    const chosen = bills.filter((b) => !b.paid && selected.has(b.id));
    if (chosen.length === 0) { toast.error("Vælg mindst én regning"); return; }
    const missing = chosen.filter((b) => !(b.creditor_account || "").trim());
    if (missing.length) { toast.error(`Mangler modtagerkonto: ${missing.map((b) => b.supplier).join(", ")}`); return; }
    if (!(bankSettings.own_account || "").trim()) { toast.error("Angiv din afsenderkonto under Bankindstillinger"); return; }
    const pc = bankSettings.product_code || "ERH356";
    const lines = chosen.map((b) => {
      const f = Array(53).fill("");
      const name = (b.supplier || "").slice(0, 32);
      const text = (b.invoice_number ? `Faktura ${b.invoice_number}` : (b.note || b.supplier || "")).slice(0, 35);
      f[0] = pc; f[1] = bankSettings.own_account.trim(); f[2] = name; f[3] = (b.creditor_account || "").replace(/\s/g, ""); f[4] = name;
      f[8] = (b.amount || 0).toFixed(2); f[9] = ddmmyyyy(b.due_date); f[10] = text; f[19] = "N"; f[32] = text;
      return f.map((x) => `"${String(x).replace(/"/g, "")}"`).join(",");
    });
    const content = lines.join("\r\n") + "\r\n";
    const bytes = new Uint8Array(content.length);
    for (let i = 0; i < content.length; i++) { const c = content.charCodeAt(i); bytes[i] = c < 256 ? c : 63; }
    const url = URL.createObjectURL(new Blob([bytes], { type: "text/plain" }));
    const a = document.createElement("a"); a.href = url;
    const d = new Date(); a.download = `bankfil_${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}.txt`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success(`Bankfil hentet (${chosen.length} betalinger)`);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const resetForm = () => { setForm(emptyForm); setEditId(null); };

  const submit = async () => {
    const amt = parseAmount(form.amount);
    if (!form.supplier.trim()) { toast.error("Indtast leverandør"); return; }
    if (!amt || amt <= 0) { toast.error("Indtast et gyldigt beløb"); return; }
    if (!form.due_date) { toast.error("Vælg en forfaldsdato"); return; }
    const body = { supplier: form.supplier.trim(), invoice_number: form.invoice_number.trim(),
      amount: amt, due_date: form.due_date, note: form.note.trim(), creditor_account: (form.creditor_account || "").trim() };
    try {
      if (editId) { await api.put(`${P}/bills/${editId}`, { ...body, paid: bills.find((b) => b.id === editId)?.paid || false }); toast.success("Regning opdateret"); }
      else { await api.post(`${P}/bills`, { ...body, paid: false }); toast.success("Regning tilføjet"); }
      if (body.creditor_account) { try { await api.post("/suppliers/save-account", { name: body.supplier, bank_account: body.creditor_account }); loadSuppliers(); } catch { /* ignore */ } }
      resetForm(); await load();
    } catch { toast.error("Kunne ikke gemme regning"); }
  };

  const startEdit = (b) => { setEditId(b.id); setForm({ supplier: b.supplier, invoice_number: b.invoice_number, amount: amountToInput(b.amount), due_date: b.due_date || "", note: b.note || "", creditor_account: b.creditor_account || "" }); };

  const copyBill = (b) => {
    setEditId(null);
    setForm({ supplier: b.supplier, invoice_number: "", amount: amountToInput(b.amount), due_date: b.due_date || "", note: b.note || "", creditor_account: b.creditor_account || "" });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info("Kopieret — indtast fakturanr. og tryk Tilføj");
  };

  const togglePaid = async (b) => {
    const next = !b.paid;
    setBills((xs) => xs.map((x) => (x.id === b.id ? { ...x, paid: next, paid_date: next ? x.paid_date : null } : x)));
    try {
      const { data } = await api.patch(`${P}/bills/${b.id}/paid`, { paid: next });
      const paidDate = data?.paid_date !== undefined ? data.paid_date : (next ? b.paid_date : null);
      const paidVal = data?.paid !== undefined ? data.paid : next;
      setBills((xs) => xs.map((x) => (x.id === b.id ? { ...x, paid: paidVal, paid_date: paidDate } : x)));
    } catch {
      setBills((xs) => xs.map((x) => (x.id === b.id ? { ...x, paid: b.paid, paid_date: b.paid_date } : x)));
      toast.error("Kunne ikke opdatere status");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Slet denne regning?")) return;
    try { await api.delete(`${P}/bills/${id}`); if (editId === id) resetForm(); await load(); toast.success("Regning slettet"); }
    catch { toast.error("Kunne ikke slette"); }
  };

  if (loading) return <p className="text-slate-500 text-sm py-8 text-center" data-testid="bills-loading">Henter…</p>;

  const today = todayMidnight();
  const unpaid = bills.filter((b) => !b.paid);
  const unpaidTotal = unpaid.reduce((s, b) => s + (b.amount || 0), 0);
  const dueSoon = unpaid.filter((b) => { const d = parse(b.due_date); return d && Math.round((d - today) / 86400000) <= 7; });
  const dueSoonTotal = dueSoon.reduce((s, b) => s + (b.amount || 0), 0);
  const overdueCount = unpaid.filter((b) => billStatus(b) === "overdue").length;
  const curMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const paidThisMonth = bills.filter((b) => b.paid && (b.paid_date || "").startsWith(curMonth)).reduce((s, b) => s + (b.amount || 0), 0);

  // --- Overblik-aggregater ---
  const overdueList = unpaid.filter((b) => billStatus(b) === "overdue");
  const overdueTotal = overdueList.reduce((s, b) => s + (b.amount || 0), 0);
  const soonOnly = unpaid.filter((b) => billStatus(b) === "soon");
  const soonOnlyTotal = soonOnly.reduce((s, b) => s + (b.amount || 0), 0);
  const openOnly = unpaid.filter((b) => billStatus(b) === "open");
  const openOnlyTotal = openOnly.reduce((s, b) => s + (b.amount || 0), 0);
  const seg = (v) => (unpaidTotal > 0 ? (v / unpaidTotal) * 100 : 0);
  const bySupplier = Object.values(unpaid.reduce((a, b) => { const k = b.supplier || "—"; if (!a[k]) a[k] = { supplier: k, total: 0, count: 0 }; a[k].total += b.amount || 0; a[k].count += 1; return a; }, {})).sort((x, y) => y.total - x.total);
  const maxSup = Math.max(...bySupplier.map((s) => s.total), 1);
  const byMonth = Object.values(unpaid.reduce((a, b) => { const d = parse(b.due_date); const k = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` : "?"; if (!a[k]) a[k] = { key: k, total: 0, count: 0 }; a[k].total += b.amount || 0; a[k].count += 1; return a; }, {})).sort((x, y) => x.key.localeCompare(y.key));
  const monthLabel = (k) => { if (k === "?") return "Uden dato"; const [y, m] = k.split("-"); return `${MON[+m - 1]} ${y}`; };
  const nowMonthKey = curMonth;

  const suppliers = [...new Set(bills.map((b) => b.supplier).filter(Boolean))].sort((a, b) => a.localeCompare(b, "da"));
  const q = query.trim().toLowerCase();
  const filtered = bills.filter((b) => {
    const st = billStatus(b);
    if (statusFilter === "unpaid" && b.paid) return false;
    if (statusFilter === "paid" && !b.paid) return false;
    if (statusFilter === "overdue" && st !== "overdue") return false;
    if (statusFilter === "soon" && st !== "soon") return false;
    if (supplierFilter !== "all" && b.supplier !== supplierFilter) return false;
    if (q && !`${b.supplier} ${b.invoice_number} ${b.note || ""}`.toLowerCase().includes(q)) return false;
    return true;
  });
  const filteredTotal = filtered.reduce((s, b) => s + (b.amount || 0), 0);
  const filtersActive = query || statusFilter !== "all" || supplierFilter !== "all";

  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  const sorted = [...filtered].sort((a, b) => {
    const k = sort.key;
    const r = k === "amount"
      ? (a.amount || 0) - (b.amount || 0)
      : String(a[k] || "").localeCompare(String(b[k] || ""), "da", { numeric: true });
    return sort.dir === "asc" ? r : -r;
  });
  const SortHead = ({ k, children, align = "left" }) => (
    <th className={`font-medium py-2 pr-2 cursor-pointer select-none hover:text-slate-600 ${align === "right" ? "text-right" : "text-left"}`} onClick={() => toggleSort(k)} data-testid={`bills-sort-${k}`}>
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>{children}{sort.key === k ? (sort.dir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}</span>
    </th>
  );

  const rowBg = (st) => st === "paid" ? "bg-emerald-500/10" : st === "overdue" ? "bg-red-500/10" : st === "soon" ? "bg-amber-500/10" : "";
  const dueDiff = (b) => { const d = parse(b.due_date); return d ? Math.round((d - today) / 86400000) : null; };
  const relDue = (n) => n == null ? "" : n === 0 ? "forfalder i dag" : n > 0 ? `om ${n} ${n === 1 ? "dag" : "dage"}` : `${Math.abs(n)} ${Math.abs(n) === 1 ? "dag" : "dage"} over`;
  const STATUS_META = {
    overdue: { label: "Forfaldet", cls: "bg-red-100 text-red-700" },
    soon: { label: "Snart", cls: "bg-amber-100 text-amber-700" },
    open: { label: "Åben", cls: "bg-sky-100 text-sky-700" },
    paid: { label: "Betalt", cls: "bg-emerald-100 text-emerald-700" },
  };

  return (
    <div className="space-y-4" data-testid="bills-stats">
      <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="bills-header">
        <Receipt className="w-4 h-4" />
        <span>Regninger · leverandørfakturaer · beløb inkl. moms · grøn = betalt</span>
      </div>

      {/* Send/upload faktura → AI-udkast */}
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid="invoice-import">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-sky-500" />
          <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Send en faktura ind</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">Upload en faktura (PDF eller billede), så læser AI leverandør, beløb, fakturanr. og forfaldsdato og laver et udkast, du kan godkende nedenfor. Du kan også sende fakturaen på email — spørg admin om opsætning.</p>
        <label className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#cbd5e1] bg-[#f8fafc] py-6 cursor-pointer hover:border-sky-400 hover:bg-sky-50/50 transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="invoice-upload-label">
          <input type="file" accept=".pdf,image/*" className="hidden" onChange={onUploadInvoice} disabled={uploading} data-testid="invoice-upload-input" />
          {uploading
            ? <><Loader2 className="w-6 h-6 text-sky-500 animate-spin" /><span className="text-sm text-slate-600">Læser faktura…</span></>
            : <><Upload className="w-6 h-6 text-slate-400" /><span className="text-sm text-slate-600 font-medium">Vælg eller træk faktura hertil</span><span className="text-xs text-slate-400">PDF, JPG eller PNG · maks. 15 MB</span></>}
        </label>
      </div>

      {/* Udkast til godkendelse */}
      {drafts.length > 0 && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 sm:p-5 space-y-4" data-testid="bill-drafts">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-amber-600" />
            <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-amber-700">Til godkendelse ({drafts.length})</h3>
            <span className="text-xs text-amber-600/80">Tjek at tallene passer, ret hvis nødvendigt, og tryk Godkend</span>
          </div>
          {drafts.map((d, i) => {
            const ed = dEdits[d.id] || {};
            const dup = findDuplicate(ed.invoice_number);
            return (
              <div key={d.id} className="rounded-xl bg-white border border-amber-200 p-4" data-testid={`bill-draft-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
                    {d.source === "email" ? <Mail className="w-3.5 h-3.5" /> : <Upload className="w-3.5 h-3.5" />}
                    {d.source === "email" ? `fra email${d.sender ? ` · ${d.sender}` : ""}` : "uploadet"}
                  </span>
                  {d.file_path && <a href={draftFileUrl(d.file_path)} target="_blank" rel="noreferrer" className="text-xs text-sky-600 hover:text-sky-700 inline-flex items-center gap-1" data-testid={`bill-draft-view-${i}`}><FileText className="w-3.5 h-3.5" /> Se faktura</a>}
                </div>
                {dup && (
                  <div className="mb-3 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700" data-testid={`bill-draft-dup-${i}`}>
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>Muligt dublet: fakturanr. <b>{ed.invoice_number}</b> findes allerede på listen ({dup.supplier}{dup.paid ? " · betalt" : ""}, {amountToInput(dup.amount)} kr.). Tjek inden du godkender.</span>
                  </div>
                )}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                  <div className="lg:col-span-2"><label className="text-[11px] text-slate-500 mb-1 block">Leverandør</label>
                    <Input value={ed.supplier || ""} onChange={(e) => setDField(d.id, "supplier", e.target.value)} className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid={`bill-draft-supplier-${i}`} /></div>
                  <div><label className="text-[11px] text-slate-500 mb-1 block">Fakturanr.</label>
                    <Input value={ed.invoice_number || ""} onChange={(e) => setDField(d.id, "invoice_number", e.target.value)} className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 tabular-nums" data-testid={`bill-draft-invoice-${i}`} /></div>
                  <div><label className="text-[11px] text-slate-500 mb-1 block">Beløb (inkl. moms)</label>
                    <Input value={ed.amount || ""} onChange={(e) => setDField(d.id, "amount", e.target.value)} placeholder="0,00" className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 tabular-nums" data-testid={`bill-draft-amount-${i}`} /></div>
                  <div><label className="text-[11px] text-slate-500 mb-1 block">Forfaldsdato</label>
                    <Input type="date" value={ed.due_date || ""} onChange={(e) => setDField(d.id, "due_date", e.target.value)} className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 tabular-nums" data-testid={`bill-draft-due-${i}`} /></div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 mt-3">
                  <div className="lg:col-span-2"><label className="text-[11px] text-slate-500 mb-1 block">Modtagerkonto (bank) — valgfri</label>
                    <Input value={ed.creditor_account || ""} onChange={(e) => setDField(d.id, "creditor_account", e.target.value)} placeholder="fx 08971003864" className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 tabular-nums" data-testid={`bill-draft-account-${i}`} /></div>
                  <div className="lg:col-span-3 flex items-end justify-end gap-2">
                    <Button variant="ghost" onClick={() => discardDraft(d.id)} className="h-9 rounded-lg text-slate-500 hover:text-red-600 gap-1.5" data-testid={`bill-draft-discard-${i}`}><X className="w-4 h-4" /> Afvis</Button>
                    <Button onClick={() => approveDraft(d)} className="h-9 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5 font-medium" data-testid={`bill-draft-approve-${i}`}><Check className="w-4 h-4" /> Godkend</Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile title="Ubetalt i alt" value={kr(unpaidTotal)} sub={`${unpaid.length} ${unpaid.length === 1 ? "regning" : "regninger"}`} color={unpaidTotal > 0 ? "#0f172a" : GREEN} testId="bills-kpi-unpaid" />
        <Tile title="Forfaldet" value={kr(overdueTotal)} icon={<AlertTriangle className="w-4 h-4" />} sub={overdueList.length ? `${overdueList.length} over forfald!` : "Intet forfaldet"} color={overdueTotal > 0 ? RED : GREEN} testId="bills-kpi-overdue" />
        <Tile title="Forfalder ≤ 7 dage" value={kr(soonOnlyTotal)} icon={<Clock className="w-4 h-4" />} sub={`${soonOnly.length} inden for 7 dage`} color={soonOnlyTotal > 0 ? AMBER : GREEN} testId="bills-kpi-duesoon" />
        <Tile title="Betalt denne måned" value={kr(paidThisMonth)} sub="Registreret som betalt" color={GREEN} testId="bills-kpi-paidmonth" />
      </div>

      {/* Overblik: fordeling + leverandører + måneder */}
      {unpaid.length > 0 && (
        <div className="space-y-4" data-testid="bills-overview">
          {/* Status-fordeling */}
          <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid="bills-distribution">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[13px] font-semibold uppercase tracking-[0.14em] text-slate-500">Fordeling af ubetalt</h3>
              <span className="text-sm font-bold tabular-nums text-slate-900">{kr(unpaidTotal)}</span>
            </div>
            <div className="h-3 rounded-full overflow-hidden flex bg-[#f1f5f9]">
              <div className="h-full" style={{ width: `${seg(overdueTotal)}%`, background: RED }} title={`Forfaldet: ${kr(overdueTotal)}`} />
              <div className="h-full" style={{ width: `${seg(soonOnlyTotal)}%`, background: AMBER }} title={`Snart: ${kr(soonOnlyTotal)}`} />
              <div className="h-full" style={{ width: `${seg(openOnlyTotal)}%`, background: "#38BDF8" }} title={`Åben: ${kr(openOnlyTotal)}`} />
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-xs">
              <span className="inline-flex items-center gap-1.5 text-slate-600"><span className="w-2.5 h-2.5 rounded-full" style={{ background: RED }} /> Forfaldet <span className="font-semibold text-slate-800 tabular-nums">{kr(overdueTotal)}</span> ({overdueList.length})</span>
              <span className="inline-flex items-center gap-1.5 text-slate-600"><span className="w-2.5 h-2.5 rounded-full" style={{ background: AMBER }} /> ≤ 7 dage <span className="font-semibold text-slate-800 tabular-nums">{kr(soonOnlyTotal)}</span> ({soonOnly.length})</span>
              <span className="inline-flex items-center gap-1.5 text-slate-600"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#38BDF8" }} /> Åben <span className="font-semibold text-slate-800 tabular-nums">{kr(openOnlyTotal)}</span> ({openOnly.length})</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Ubetalt pr. leverandør */}
            <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid="bills-by-supplier">
              <h3 className="text-[13px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">Ubetalt pr. leverandør</h3>
              <ul className="space-y-2.5">
                {bySupplier.slice(0, 8).map((s) => (
                  <li key={s.supplier} data-testid={`bills-supplier-${s.supplier}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <button onClick={() => setSupplierFilter(s.supplier)} className="text-slate-700 truncate pr-2 hover:text-sky-700 hover:underline text-left">{s.supplier} <span className="text-slate-400">({s.count})</span></button>
                      <span className="font-semibold tabular-nums text-slate-800 whitespace-nowrap">{kr(s.total)}</span>
                    </div>
                    <div className="h-1.5 rounded bg-[#f1f5f9] overflow-hidden">
                      <div className="h-full rounded" style={{ width: `${(s.total / maxSup) * 100}%`, background: "#38BDF8" }} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Forfald pr. måned */}
            <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid="bills-by-month">
              <h3 className="text-[13px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">Forfald pr. måned</h3>
              <ul className="divide-y divide-[#f1f5f9]">
                {byMonth.map((m) => {
                  const isOverdueMonth = m.key !== "?" && m.key < nowMonthKey;
                  const isThisMonth = m.key === nowMonthKey;
                  return (
                    <li key={m.key} className="flex items-center justify-between py-2.5" data-testid={`bills-month-${m.key}`}>
                      <span className="flex items-center gap-2 text-sm">
                        <span className={`w-2 h-2 rounded-full ${isOverdueMonth ? "bg-red-500" : isThisMonth ? "bg-amber-400" : "bg-sky-400"}`} />
                        <span className={`capitalize ${isOverdueMonth ? "text-red-600 font-medium" : "text-slate-700"}`}>{monthLabel(m.key)}</span>
                        <span className="text-xs text-slate-400">({m.count})</span>
                      </span>
                      <span className="font-semibold tabular-nums text-slate-800">{kr(m.total)}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Betal via bank */}
      {unpaid.length > 0 && (
        <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid="bills-bank-export">
          <div className="flex items-center gap-2 mb-2">
            <Landmark className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Betal via bank</h3>
            <button onClick={openBankSettings} className="ml-auto text-xs text-slate-400 hover:text-slate-700 inline-flex items-center gap-1" data-testid="bank-settings-btn"><Settings className="w-3.5 h-3.5" /> Bankindstillinger</button>
          </div>
          <p className="text-xs text-slate-500 mb-3">Sæt flueben i kolonnen "Betal" i listen nedenfor, og hent en fil du kan oplade i banken. Regninger uden modtagerkonto kan ikke vælges.</p>
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#e2e8f0]">
            <span className="text-sm text-slate-500">{selected.size} valgt · <span className="font-bold text-slate-900 tabular-nums">{kr(bills.filter((b) => !b.paid && selected.has(b.id)).reduce((s, b) => s + (b.amount || 0), 0))}</span></span>
            <Button onClick={buildBankFile} disabled={selected.size === 0} className="h-9 rounded-lg bg-[#38BDF8] hover:bg-sky-400 text-[#0b1120] gap-1.5 font-medium" data-testid="bank-export-btn"><Download className="w-4 h-4" /> Hent bankfil ({selected.size})</Button>
          </div>
        </div>
      )}

      <Dialog open={bankDlg} onOpenChange={setBankDlg}>
        <DialogContent className="bg-white border-[#e2e8f0] text-slate-800 max-w-md" data-testid="bank-settings-dialog">
          <DialogHeader><DialogTitle className="font-heading font-light">Bankindstillinger</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-xs text-slate-500 mb-1 block">Din afsenderkonto (reg+konto)</label>
              <Input value={bankForm.own_account} onChange={(e) => setBankForm((f) => ({ ...f, own_account: e.target.value }))} placeholder="68580001110332" className="bg-[#f8fafc] border-[#e2e8f0] text-slate-700 tabular-nums" data-testid="bank-own-account" /></div>
            <div><label className="text-xs text-slate-500 mb-1 block">Produktkode</label>
              <Input value={bankForm.product_code} onChange={(e) => setBankForm((f) => ({ ...f, product_code: e.target.value }))} placeholder="ERH356" className="bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="bank-product-code" /></div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setBankDlg(false)} className="text-slate-500">Annullér</Button>
            <Button onClick={saveBankSettings} className="rounded-lg bg-[#38BDF8] text-[#0b1120] hover:bg-sky-400" data-testid="bank-settings-save">Gem</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / edit form */}
      <div className={`rounded-2xl bg-[#ffffff] border p-4 sm:p-6 ${editId ? "border-sky-300 ring-1 ring-sky-100" : "border-[#e2e8f0]"}`} data-testid="bill-editor">
        <div className="flex items-center gap-2 mb-5">
          <span className="w-8 h-8 rounded-lg bg-[#38BDF8]/15 flex items-center justify-center flex-shrink-0"><Plus className="w-4 h-4 text-sky-700" /></span>
          <div>
            <h3 className="font-heading text-base font-semibold text-slate-900">{editId ? "Redigér regning" : "Tilføj ny regning"}</h3>
            <p className="text-xs text-slate-500">Beløb inkl. moms · felter med * er påkrævet</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-4">
          <div className="sm:col-span-2 lg:col-span-1">
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Leverandør *</label>
            <Input list="bill-suppliers" value={form.supplier} onChange={(e) => onSupplierChange(e.target.value)} placeholder="fx Dagrofa" className="h-10 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="bill-supplier-input" />
            <datalist id="bill-suppliers">{suppliers.map((s) => <option key={s} value={s} />)}</datalist>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Fakturanr.</label>
            <Input value={form.invoice_number} onChange={(e) => setField("invoice_number", e.target.value)} placeholder="valgfri" className="h-10 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="bill-invoice-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Beløb (inkl. moms) *</label>
            <div className="relative">
              <Input value={form.amount} onChange={(e) => setField("amount", e.target.value)} inputMode="decimal" placeholder="0,00" className="h-10 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 pr-9 tabular-nums" data-testid="bill-amount-input" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none">kr.</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Forfaldsdato *</label>
            <Input type="date" value={form.due_date} onChange={(e) => setField("due_date", e.target.value)} className="h-10 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="bill-due-input" />
          </div>
          <div className="sm:col-span-2 lg:col-span-2">
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Note</label>
            <Input value={form.note} onChange={(e) => setField("note", e.target.value)} placeholder="fx hvad regningen dækker (valgfri)" onKeyDown={(e) => e.key === "Enter" && submit()} className="h-10 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="bill-note-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Modtagerkonto (bank)</label>
            <Input value={form.creditor_account} onChange={(e) => setField("creditor_account", e.target.value)} placeholder="fx 08971003864" className="h-10 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 tabular-nums" data-testid="bill-creditor-input" />
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={submit} className="h-10 rounded-lg bg-[#38BDF8] hover:bg-sky-400 text-[#0b1120] gap-1.5 flex-1 font-medium" data-testid="bill-add-btn">
              {editId ? <><Check className="w-4 h-4" /> Gem ændringer</> : <><Plus className="w-4 h-4" /> Tilføj regning</>}
            </Button>
            {editId && <Button variant="outline" size="icon" onClick={resetForm} className="h-10 w-10 rounded-lg bg-white border-[#e2e8f0] text-slate-500" data-testid="bill-cancel-btn"><X className="w-4 h-4" /></Button>}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5">
        {bills.length === 0 ? (
          <p className="text-slate-500 text-sm py-8 text-center" data-testid="bills-empty">Ingen regninger tastet ind endnu. Tilføj din første ovenfor.</p>
        ) : (
          <>
            {/* Filter + search bar */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-4" data-testid="bills-filter-bar">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Søg leverandør, fakturanr. eller note…" className="h-9 pl-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="bills-search-input" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-700 sm:w-[170px]" data-testid="bills-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-lg bg-[#ffffff] border-[#e2e8f0] text-slate-700">
                  <SelectItem value="all">Alle status</SelectItem>
                  <SelectItem value="unpaid">Ubetalt</SelectItem>
                  <SelectItem value="overdue">Forfaldet</SelectItem>
                  <SelectItem value="soon">Forfalder snart</SelectItem>
                  <SelectItem value="paid">Betalt</SelectItem>
                </SelectContent>
              </Select>
              <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                <SelectTrigger className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-700 sm:w-[190px]" data-testid="bills-supplier-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-lg bg-[#ffffff] border-[#e2e8f0] text-slate-700 max-h-72">
                  <SelectItem value="all">Alle leverandører</SelectItem>
                  {suppliers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              {filtersActive && (
                <Button variant="ghost" onClick={() => { setQuery(""); setStatusFilter("all"); setSupplierFilter("all"); }} className="h-9 rounded-lg text-slate-500 hover:text-slate-900 gap-1.5" data-testid="bills-clear-filters"><X className="w-4 h-4" /> Ryd</Button>
              )}
            </div>

            {filtered.length === 0 ? (
              <p className="text-slate-500 text-sm py-8 text-center" data-testid="bills-no-match">Ingen regninger matcher filteret.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[820px]" data-testid="bills-table">
                  <thead>
                    <tr className="text-slate-500 text-xs uppercase tracking-wider border-b border-[#e2e8f0]">
                      <th className="text-left font-medium py-2 pr-2 w-8" title="Vælg til bankbetaling">
                        <button onClick={toggleAllPayable} data-testid="bank-select-all" className="align-middle">
                          {(() => { const ids = bills.filter((b) => !b.paid && (b.creditor_account || "").trim()).map((b) => b.id); const all = ids.length > 0 && ids.every((id) => selected.has(id)); return all ? <CheckSquare className="w-4 h-4 text-sky-600" /> : <Square className="w-4 h-4 text-slate-300" />; })()}
                        </button>
                      </th>
                      <th className="text-left font-medium py-2 pr-2 w-10">Betalt</th>
                      <SortHead k="supplier">Leverandør</SortHead>
                      <SortHead k="invoice_number">Fakturanr.</SortHead>
                      <SortHead k="due_date">Forfald</SortHead>
                      <th className="text-left font-medium py-2 pr-2 w-24">Status</th>
                      <SortHead k="amount" align="right">Beløb</SortHead>
                      <th className="text-left font-medium py-2 pr-2">Note</th>
                      <th className="text-right font-medium py-2 w-24">Handling</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((b, i) => {
                      const st = billStatus(b);
                      return (
                        <tr key={b.id} className={`border-b border-[#e2e8f0] ${selected.has(b.id) ? "bg-sky-50" : rowBg(st)}`} data-testid={`bill-row-${i}`}>
                          <td className="py-2.5 pr-2">
                            {b.paid
                              ? <span className="inline-block w-5 h-5" title="Betalt" />
                              : (b.creditor_account || "").trim()
                              ? <button onClick={() => toggleSel(b.id)} data-testid={`bill-select-${i}`}>{selected.has(b.id) ? <CheckSquare className="w-5 h-5 text-sky-600" /> : <Square className="w-5 h-5 text-slate-300 hover:text-sky-400" />}</button>
                              : acctEdit === b.id
                              ? <div className="flex items-center gap-1">
                                  <Input autoFocus value={acctVal} onChange={(e) => setAcctVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveAccount(b, acctVal); if (e.key === "Escape") { setAcctEdit(null); setAcctVal(""); } }} placeholder="Kontonr." className="h-7 w-28 text-xs bg-[#f8fafc] border-[#e2e8f0] text-slate-800 tabular-nums" data-testid={`bill-acct-input-${i}`} />
                                  <button onClick={() => saveAccount(b, acctVal)} className="text-emerald-600 hover:text-emerald-700" title="Gem kontonr." data-testid={`bill-acct-save-${i}`}><Check className="w-4 h-4" /></button>
                                  <button onClick={() => { setAcctEdit(null); setAcctVal(""); }} className="text-slate-400 hover:text-slate-600" title="Annullér"><X className="w-4 h-4" /></button>
                                </div>
                              : <button onClick={() => { setAcctEdit(b.id); setAcctVal(""); }} title="Tilføj modtagerkonto" className="inline-flex items-center gap-1 text-[11px] text-sky-600 hover:text-sky-700 font-medium whitespace-nowrap" data-testid={`bill-add-acct-${i}`}><Plus className="w-3.5 h-3.5" />Kontonr.</button>}
                          </td>
                          <td className="py-2.5 pr-2">
                            <button onClick={() => togglePaid(b)} data-testid={`bill-paid-toggle-${i}`}
                              className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${b.paid ? "bg-emerald-500 border-emerald-500 text-[#0b1120]" : "border-[#cbd5e1] text-transparent hover:border-slate-400"}`}
                              title={b.paid ? "Markér som ubetalt" : "Markér som betalt"}>
                              <Check className="w-3.5 h-3.5" />
                            </button>
                          </td>
                          <td className="py-2.5 pr-2 text-slate-800 font-medium">{b.supplier}</td>
                          <td className="py-2.5 pr-2 text-slate-500 tabular-nums">{b.invoice_number || "—"}</td>
                          <td className="py-2.5 pr-2">
                            <span className="flex items-center gap-1.5 tabular-nums font-medium" style={{ color: st === "overdue" ? RED : st === "soon" ? "#B45309" : "#334155" }}>
                              {st === "overdue" && <AlertTriangle className="w-3.5 h-3.5" />}
                              {fmtDate(b.due_date)}
                            </span>
                            {b.paid && b.paid_date
                              ? <span className="block text-[11px] text-emerald-600/70">betalt {fmtDate(b.paid_date)}</span>
                              : !b.paid && dueDiff(b) != null && <span className={`block text-[11px] ${st === "overdue" ? "text-red-500" : st === "soon" ? "text-amber-600" : "text-slate-400"}`}>{relDue(dueDiff(b))}</span>}
                          </td>
                          <td className="py-2.5 pr-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_META[st].cls}`} data-testid={`bill-status-${i}`}>{STATUS_META[st].label}</span>
                          </td>
                          <td className="py-2.5 pr-2 text-right tabular-nums font-semibold text-slate-800">{kr(b.amount)}</td>
                          <td className="py-2.5 pr-2 text-slate-500 max-w-[180px] truncate" title={b.note}>{b.note || "—"}</td>
                          <td className="py-2.5 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button className="text-slate-500 hover:text-[#38BDF8] p-1" onClick={() => copyBill(b)} title="Kopiér som ny regning" data-testid={`bill-copy-${i}`}><Copy className="w-3.5 h-3.5" /></button>
                              <button className="text-slate-500 hover:text-slate-900 p-1" onClick={() => startEdit(b)} data-testid={`bill-edit-${i}`}><Pencil className="w-3.5 h-3.5" /></button>
                              <button className="text-slate-500 hover:text-[#dc2626] p-1" onClick={() => remove(b.id)} data-testid={`bill-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="text-slate-500 font-medium border-t border-[#e2e8f0]">
                      <td className="py-2.5"></td>
                      <td className="py-2.5"></td>
                      <td className="py-2.5" data-testid="bills-filtered-count">I alt ({filtersActive ? `${filtered.length} af ${bills.length}` : bills.length})</td>
                      <td></td><td></td><td></td>
                      <td className="py-2.5 text-right tabular-nums text-slate-800 font-semibold" data-testid="bills-total-row">{kr(filteredTotal)}</td>
                      <td></td><td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
